import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  BookOpen,
  CheckCircle2,
  Clock,
  Coins,
  Gavel,
  LineChart,
  ListChecks,
  Shield,
  Sparkles,
  Target,
  Users,
  Zap,
} from 'lucide-react';

export default function BlogPolymarketUltimateGuide() {
  return (
    <div className="min-h-screen bg-dark-900 text-dark-100">
      <div className="max-w-4xl mx-auto px-4 py-10 md:py-14 space-y-10">
        <header className="space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-2">
              <p className="text-xs uppercase tracking-[0.2em] text-primary-400 flex items-center gap-2">
                <span className="inline-flex h-1.5 w-1.5 rounded-full bg-primary-400" />
                Polymarket guide
              </p>
              <h1 className="text-2xl md:text-3xl font-bold leading-tight">
                The Ultimate Polymarket Guide (2026): How It Works, How to Trade, and How to Copy Traders Safely
              </h1>
              <p className="text-sm md:text-base text-dark-300 max-w-2xl">
                Polymarket is a real-money prediction market where prices behave like probabilities and the best
                traders are effectively trading information. This guide is a complete walkthrough: from the YES/NO
                share mechanics, to order books and liquidity, to risk management, paper trading, and how settlement
                and redeem actually work.
              </p>
            </div>

            <div className="hidden sm:flex flex-col items-end gap-2 text-xs text-dark-400">
              <div className="inline-flex items-center gap-1">
                <Clock className="w-4 h-4" />
                <span>25–35 min read</span>
              </div>
              <div className="inline-flex items-center gap-1">
                <BookOpen className="w-4 h-4 text-primary-300" />
                <span>One pillar article</span>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-3 text-xs text-dark-400">
            <Link
              to="/blog"
              className="inline-flex items-center gap-1.5 text-primary-400 hover:text-primary-300 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to all articles
            </Link>
            <span className="hidden sm:inline">·</span>
            <span>Goal: understand Polymarket deeply and avoid beginner traps.</span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 text-xs text-dark-300 bg-dark-800/70 border border-dark-700 rounded-2xl p-4 mt-2">
            <div className="space-y-1">
              <p className="font-semibold text-dark-100 flex items-center gap-1.5">
                <ListChecks className="w-4 h-4 text-primary-400" />
                What you&apos;ll learn
              </p>
              <ul className="list-disc list-inside space-y-1">
                <li>How YES/NO shares work and why price is probability.</li>
                <li>How the CLOB works: limit orders, liquidity, slippage.</li>
                <li>How to copy traders safely (and what to avoid).</li>
              </ul>
            </div>
            <div className="space-y-1">
              <p className="font-semibold text-dark-100">Who this is for</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Beginners: want a real Polymarket primer.</li>
                <li>Degens: want a system, not vibes.</li>
                <li>Copy traders: want real guardrails and process.</li>
              </ul>
            </div>
            <div className="space-y-1">
              <p className="font-semibold text-dark-100">Quick outcome</p>
              <ul className="list-disc list-inside space-y-1">
                <li>You can read any market and understand the risks.</li>
                <li>You can place orders without donating spread.</li>
                <li>You know what happens when a market resolves.</li>
              </ul>
            </div>
          </div>
        </header>

        <main className="space-y-10 text-sm md:text-base leading-relaxed text-dark-200">
          <section className="space-y-4 bg-dark-800/70 border border-dark-700 rounded-2xl p-4 md:p-5">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary-400" />
              <h2 className="text-sm md:text-base font-semibold">Table of contents</h2>
            </div>
            <nav className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
              <a className="text-primary-400 hover:text-primary-300" href="#polymarket-basics">
                1) Polymarket basics: what you&apos;re actually trading
              </a>
              <a className="text-primary-400 hover:text-primary-300" href="#order-book">
                2) The CLOB: limit orders, liquidity, and slippage
              </a>
              <a className="text-primary-400 hover:text-primary-300" href="#strategies">
                3) Core strategy archetypes (and when each breaks)
              </a>
              <a className="text-primary-400 hover:text-primary-300" href="#risk">
                4) Risk management that survives real variance
              </a>
              <a className="text-primary-400 hover:text-primary-300" href="#copy-trading">
                5) Copy trading: how to choose traders and not get farmed
              </a>
              <a className="text-primary-400 hover:text-primary-300" href="#paper">
                6) Paper trading: how to test without lying to yourself
              </a>
              <a className="text-primary-400 hover:text-primary-300" href="#resolution">
                7) Resolution + redeem: why positions look weird sometimes
              </a>
              <a className="text-primary-400 hover:text-primary-300" href="#faq">
                8) FAQ
              </a>
            </nav>
          </section>

          <section id="polymarket-basics" className="space-y-4">
            <h2 className="text-lg md:text-xl font-semibold flex items-center gap-2">
              <Target className="w-5 h-5 text-primary-400" />
              1) Polymarket basics: what you&apos;re actually trading
            </h2>
            <p>
              On Polymarket you&apos;re not placing a sportsbook bet. You&apos;re trading two outcomes (YES and NO)
              as tokens on a live order book. Each share is worth between <span className="font-mono">0</span> and{' '}
              <span className="font-mono">1</span> USDC.e at resolution.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="bg-dark-800/60 border border-dark-700 rounded-2xl p-4 space-y-2">
                <p className="font-semibold text-dark-100 flex items-center gap-2">
                  <LineChart className="w-4 h-4 text-primary-400" />
                  Price = probability (roughly)
                </p>
                <p className="text-sm text-dark-300">
                  A YES share at <span className="font-mono">0.62</span> implies the market is pricing about a 62%
                  chance the event happens. That&apos;s not truth — it&apos;s the current equilibrium of money and
                  information.
                </p>
              </div>
              <div className="bg-dark-800/60 border border-dark-700 rounded-2xl p-4 space-y-2">
                <p className="font-semibold text-dark-100 flex items-center gap-2">
                  <Coins className="w-4 h-4 text-primary-400" />
                  PnL is simple per share
                </p>
                <p className="text-sm text-dark-300">
                  If you buy YES at <span className="font-mono">0.30</span> and it resolves YES, you redeem for{' '}
                  <span className="font-mono">1.00</span>. Profit is <span className="font-mono">0.70</span> per share
                  (before fees).
                </p>
              </div>
            </div>
            <p>
              The practical implication: Polymarket is an information market. You are constantly making one bet:
              <span className="font-medium"> is the true probability higher or lower than the current price?</span>
            </p>
            <p className="text-sm text-dark-300">
              If you want the full copy trading walkthrough with BetMimic, start here:{' '}
              <Link className="text-primary-400 hover:text-primary-300" to="/blog/polymarket-copy-trading">
                Polymarket Copy Trading with BetMimic
              </Link>
              .
            </p>
          </section>

          <section id="order-book" className="space-y-4">
            <h2 className="text-lg md:text-xl font-semibold flex items-center gap-2">
              <Zap className="w-5 h-5 text-primary-400" />
              2) The order book (CLOB): limit orders, liquidity, and slippage
            </h2>
            <p>
              Polymarket runs a central limit order book (CLOB). That means your execution price depends on the orders
              currently sitting on the book.
            </p>
            <div className="space-y-3">
              <div className="bg-dark-800/60 border border-dark-700 rounded-2xl p-4">
                <p className="font-semibold text-dark-100">Limit orders</p>
                <p className="text-sm text-dark-300">
                  You choose a price and wait. You&apos;ll often get better fills, but you may not get filled at all.
                  Limits are how disciplined traders avoid donating spread.
                </p>
              </div>
              <div className="bg-dark-800/60 border border-dark-700 rounded-2xl p-4">
                <p className="font-semibold text-dark-100">Market-like orders (crossing the spread)</p>
                <p className="text-sm text-dark-300">
                  You prioritize getting filled now. You typically pay the spread and can experience slippage if there
                  isn&apos;t enough size at the best price.
                </p>
              </div>
            </div>
            <p>
              Two beginner mistakes create most painful losses:
            </p>
            <ul className="list-disc list-inside space-y-1">
              <li>
                <span className="font-medium">Trading illiquid markets with big size</span> (you become the market).
              </li>
              <li>
                <span className="font-medium">Chasing</span> after price moved (you buy after the information already got priced).
              </li>
            </ul>
            <p className="text-sm text-dark-300">
              If your goal is automation, the tool you use must respect execution reality: don&apos;t say you&apos;re buying at
              <span className="font-mono">0.52</span> if the book means you&apos;ll actually fill at <span className="font-mono">0.58</span>.
              That&apos;s how people blow through risk limits without noticing.
            </p>
          </section>

          <section id="strategies" className="space-y-4">
            <h2 className="text-lg md:text-xl font-semibold flex items-center gap-2">
              <LineChart className="w-5 h-5 text-primary-400" />
              3) Core Polymarket strategy archetypes (and when each breaks)
            </h2>
            <p>
              Most profitable Polymarket trading styles collapse into a few archetypes. None are magic. They all have
              failure modes.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="bg-dark-800/60 border border-dark-700 rounded-2xl p-4 space-y-2">
                <p className="font-semibold text-dark-100">Information trading</p>
                <p className="text-sm text-dark-300">
                  You have a real edge: domain knowledge, better sources, faster reaction. You enter before the crowd.
                  Fails when you&apos;re late or your info is wrong.
                </p>
              </div>
              <div className="bg-dark-800/60 border border-dark-700 rounded-2xl p-4 space-y-2">
                <p className="font-semibold text-dark-100">Market making / spread capture</p>
                <p className="text-sm text-dark-300">
                  You place bids and asks to earn spread. Fails in fast news: you become free liquidity for insiders.
                </p>
              </div>
              <div className="bg-dark-800/60 border border-dark-700 rounded-2xl p-4 space-y-2">
                <p className="font-semibold text-dark-100">Mean reversion / overreaction fades</p>
                <p className="text-sm text-dark-300">
                  You fade emotional spikes. Fails when the spike is actually real information.
                </p>
              </div>
              <div className="bg-dark-800/60 border border-dark-700 rounded-2xl p-4 space-y-2">
                <p className="font-semibold text-dark-100">Portfolio hedging</p>
                <p className="text-sm text-dark-300">
                  You use Polymarket to hedge macro/political outcomes. Fails when you oversize and the hedge becomes a
                  speculative bet.
                </p>
              </div>
            </div>
            <p className="text-sm text-dark-300">
              If you want concrete setups that fit these archetypes, read:{' '}
              <Link className="text-primary-400 hover:text-primary-300" to="/blog/polymarket-copy-trading-strategies">
                Polymarket Copy Trading Strategies
              </Link>
              .
            </p>
          </section>

          <section id="risk" className="space-y-4">
            <h2 className="text-lg md:text-xl font-semibold flex items-center gap-2">
              <Shield className="w-5 h-5 text-emerald-400" />
              4) Risk management that survives real variance
            </h2>
            <p>
              The number one reason people lose money on Polymarket isn&apos;t that they never have an edge — it&apos;s that they
              can&apos;t survive variance long enough for any edge to matter.
            </p>
            <ul className="list-disc list-inside space-y-1">
              <li>
                <span className="font-medium">Per-market max size</span>: cap how much any single question can hurt you.
              </li>
              <li>
                <span className="font-medium">Per-trader max exposure</span>: if you copy, one wallet should never control your account.
              </li>
              <li>
                <span className="font-medium">Daily loss limits</span>: your brain gets worse after losses; make the system stop you.
              </li>
              <li>
                <span className="font-medium">Price discipline</span>: set min/max prices so you don&apos;t chase longshots or buy at the top.
              </li>
            </ul>
            <div className="bg-dark-800/70 border border-dark-700 rounded-2xl p-4 md:p-5 space-y-2">
              <p className="font-semibold text-dark-100 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                Non-negotiable rule
              </p>
              <p className="text-sm text-dark-300">
                Any automation tool is only safe if it enforces your risk rules against the{' '}
                <span className="font-medium">real executable price</span>, not just the headline price on a chart.
              </p>
            </div>
            <p className="text-sm text-dark-300">
              Deep dive on real guardrails:{' '}
              <Link className="text-primary-400 hover:text-primary-300" to="/blog/polymarket-risk-management">
                Risk Management on Polymarket
              </Link>
              .
            </p>
          </section>

          <section id="copy-trading" className="space-y-4">
            <h2 className="text-lg md:text-xl font-semibold flex items-center gap-2">
              <Users className="w-5 h-5 text-primary-400" />
              5) Copy trading: how to choose traders and not get farmed
            </h2>
            <p>
              Copy trading can be a cheat code or a disaster. The difference is selection + constraints.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="bg-dark-800/60 border border-dark-700 rounded-2xl p-4 space-y-2">
                <p className="font-semibold text-dark-100">What you want in a trader</p>
                <ul className="text-sm text-dark-300 list-disc list-inside space-y-1">
                  <li>Consistent behavior across many markets (not one viral win).</li>
                  <li>Evidence of exits (not just entry bragging).</li>
                  <li>Markets where liquidity is real, not cosmetic.</li>
                </ul>
              </div>
              <div className="bg-dark-800/60 border border-dark-700 rounded-2xl p-4 space-y-2">
                <p className="font-semibold text-dark-100">What gets you farmed</p>
                <ul className="text-sm text-dark-300 list-disc list-inside space-y-1">
                  <li>Copying whales without scaling rules.</li>
                  <li>Copying hyperactive scalpers in thin markets.</li>
                  <li>Copying after you discover them (late).</li>
                </ul>
              </div>
            </div>
            <p className="text-sm text-dark-300">
              Full selection framework:{' '}
              <Link className="text-primary-400 hover:text-primary-300" to="/blog/polymarket-choosing-traders">
                How to Choose Polymarket Traders to Copy
              </Link>
              .
            </p>
            <p className="text-sm text-dark-300">
              If you&apos;re new, start with small fixed sizing and hard max exposure, then scale only after a real sample.
            </p>
          </section>

          <section id="paper" className="space-y-4">
            <h2 className="text-lg md:text-xl font-semibold flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-primary-400" />
              6) Paper trading: how to test without lying to yourself
            </h2>
            <p>
              Most people use paper trading as permission to fantasize. A useful paper trading process is closer to a
              lab: you run a hypothesis, measure it, and decide if it survives reality.
            </p>
            <ul className="list-disc list-inside space-y-1">
              <li>
                Track entry price vs executable price (what the book would have actually filled).
              </li>
              <li>
                Track whether you&apos;re implicitly taking correlated exposure across many markets.
              </li>
              <li>
                Track the true reason for wins (edge) vs random resolution variance.
              </li>
            </ul>
            <p className="text-sm text-dark-300">
              Practical workflow:{' '}
              <Link className="text-primary-400 hover:text-primary-300" to="/blog/polymarket-paper-trading">
                Paper Trading on Polymarket
              </Link>
              .
            </p>
          </section>

          <section id="resolution" className="space-y-4">
            <h2 className="text-lg md:text-xl font-semibold flex items-center gap-2">
              <Gavel className="w-5 h-5 text-primary-400" />
              7) Resolution + redeem: why positions sometimes look weird
            </h2>
            <p>
              Polymarket markets end in settlement. After resolution, prices can stop updating, some endpoints can look
              broken, and positions can appear stuck until redeem happens.
            </p>
            <div className="bg-dark-800/70 border border-dark-700 rounded-2xl p-4 md:p-5 space-y-2">
              <p className="font-semibold text-dark-100">The mental model</p>
              <ul className="text-sm text-dark-300 list-disc list-inside space-y-1">
                <li>
                  <span className="font-medium">Resolved</span>: the outcome is decided.
                </li>
                <li>
                  <span className="font-medium">Redeemable</span>: your winning shares can be claimed for USDC.e.
                </li>
                <li>
                  <span className="font-medium">Redeemed</span>: you claimed, and capital is back in your wallet.
                </li>
              </ul>
            </div>
            <p className="text-sm text-dark-300">
              Full explanation + what to do if things look off:{' '}
              <Link className="text-primary-400 hover:text-primary-300" to="/blog/polymarket-resolution-redeem">
                Market Resolved? How Redeem Works
              </Link>
              .
            </p>
          </section>

          <section id="faq" className="space-y-4">
            <h2 className="text-lg md:text-xl font-semibold">8) FAQ</h2>
            <div className="space-y-4">
              <div className="bg-dark-800/60 border border-dark-700 rounded-2xl p-4">
                <p className="font-semibold text-dark-100">Is Polymarket gambling or trading?</p>
                <p className="text-sm text-dark-300">
                  Mechanically it&apos;s trading: an order book with prices and exits. Psychologically it can become gambling
                  if you don&apos;t enforce sizing and risk rules.
                </p>
              </div>
              <div className="bg-dark-800/60 border border-dark-700 rounded-2xl p-4">
                <p className="font-semibold text-dark-100">What is the single biggest beginner mistake?</p>
                <p className="text-sm text-dark-300">
                  Oversizing illiquid markets. You end up paying spread and slippage, then blaming resolution when the
                  real issue was execution.
                </p>
              </div>
              <div className="bg-dark-800/60 border border-dark-700 rounded-2xl p-4">
                <p className="font-semibold text-dark-100">Can I copy traders while still trading manually?</p>
                <p className="text-sm text-dark-300">
                  Yes — but you need to think in terms of total exposure. Manual + copied trades can silently stack the
                  same narrative risk.
                </p>
              </div>
            </div>
          </section>

          <section className="space-y-4 border-t border-dark-800 pt-6">
            <p className="text-sm text-dark-400">
              If you want the most direct &quot;start here&quot; path:
            </p>
            <div className="flex flex-wrap gap-3 text-sm">
              <Link
                to="/blog/polymarket-copy-trading-playbook"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary-600/80 hover:bg-primary-500 transition-colors"
              >
                <BookOpen className="w-4 h-4" />
                Read the Copy Trading Playbook
              </Link>
              <Link
                to="/traders"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-dark-800 border border-dark-600 hover:border-primary-500 transition-colors"
              >
                <Users className="w-4 h-4" />
                Browse traders
              </Link>
              <Link
                to="/copy-configs"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-dark-800 border border-dark-600 hover:border-primary-500 transition-colors"
              >
                <Shield className="w-4 h-4" />
                Build a risk-managed config
              </Link>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}
